/**
 * ConsultantEvaluationPage - Full-page evaluation for a coordinator rating the client and team
 *
 * Accessible at /projects/:id/evaluate/consultant
 *
 * Shown to coordinators when the project is completed. Provides a full-page
 * experience with large centered cards, 56px avatars, and star ratings for
 * the client and each team member. On submit, calls useSubmitCoordinatorRatings.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useProjectView,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { useAuthStore } from '@/stores/authStore';
import { profileImageUrl } from '@/hooks/useProfile';
import { useVoteMembers, useSubmitCoordinatorRatings } from '@hooks/useVotes';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { Button, Spinner } from '@components/ui';
import type { VoteMember } from '@/types/index';

export default function ConsultantEvaluationPage(): JSX.Element {
  const { workerRole, clientRole } = useDaoConfig();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: projectData, isLoading: loadingProject } =
    useProjectView(projectId);
  const membersQuery = useVoteMembers(projectId);
  const { data: voteData, isLoading: loadingMembers } = membersQuery;
  const submitRatings = useSubmitCoordinatorRatings();

  const [clientRating, setClientRating] = useState(0);
  const [teamRatings, setTeamRatings] = useState<Record<string, number>>({});

  const project = projectData;
  const session = useAuthStore((s) => s.session);
  const participants = useProjectParticipants(projectId ?? '');
  const client = participants.data?.client;

  const handleTeamRating = useCallback((userId: string, score: number) => {
    setTeamRatings((prev) => ({ ...prev, [userId]: score }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!projectId) return;

    const ratings: Array<[string, number]> = (voteData?.members ?? [])
      .filter((m): m is VoteMember & { userId: string } => !!m.userId)
      .map((m) => [m.userId, teamRatings[m.userId] ?? 0]);

    submitRatings.mutate(
      { projectId, clientRating, teamRatings: ratings },
      {
        onSuccess: () => {
          navigate(`/projects/${projectId}`);
        },
      }
    );
  }, [submitRatings, projectId, clientRating, teamRatings, voteData, navigate]);

  const allRated =
    clientRating > 0 &&
    (voteData?.members ?? []).every(
      (m) => !m.userId || (teamRatings[m.userId] ?? 0) > 0
    );

  const isLoading = loadingProject || loadingMembers;

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--base-surface-1,#141414)]">
        <Spinner size="lg" />
      </div>
    );
  }

  // ---- Error ----
  if (!project) {
    return (
      <div className="min-h-screen bg-[var(--base-surface-1,#141414)] px-8 py-10 lg:px-[112px]">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back
        </button>
        <div className="py-16 text-center">
          <i className="ri-error-warning-line mb-4 text-4xl text-red-400" />
          <h2 className="text-xl font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
            Project not found
          </h2>
        </div>
      </div>
    );
  }

  if (participants.isPending)
    return (
      <div role="status" className="p-10">
        <Spinner /> Loading participants…
      </div>
    );
  if (participants.isError || membersQuery.isError)
    return (
      <div className="p-10">
        <p role="alert">
          {participants.error?.message ?? membersQuery.error?.message}
        </p>
        <Button
          onClick={() => {
            void participants.refetch();
            void membersQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  if (!project.completed || session?.accountId !== project.coordinator)
    return (
      <p role="alert" className="p-10">
        This evaluation is available to its project participant after all
        milestones complete.
      </p>
    );
  if (project.evaluations.some((e) => e.author === session?.accountId))
    return (
      <div className="p-10">
        <p role="status">Your evaluation has been saved.</p>
        <Button onClick={() => navigate('/projects/' + project.projectId)}>
          Back to project
        </Button>
      </div>
    );
  const members = voteData?.members ?? [];
  const clientAvatarUrl =
    client?.clientImage && client.profiles
      ? profileImageUrl(client.profiles.principalId, 'client')
      : undefined;

  return (
    <div className="min-h-screen bg-[var(--base-surface-1,#141414)]">
      <div className="mx-auto max-w-2xl px-8 py-10 lg:px-[112px]">
        {/* Back navigation */}
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back to Project
        </button>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(54,211,153,0.15)]">
            <i className="ri-star-line text-3xl text-[var(--state-brand-active,#36d399)]" />
          </div>
          <h1 className="mb-2 text-[30px] font-bold leading-[42px] text-[var(--text-dark-primary,#f5f5f5)]">
            Rate Project Participants
          </h1>
          <p className="text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            The project{' '}
            <span className="font-medium text-[var(--state-brand-active,#36d399)]">
              {project.title}
            </span>{' '}
            has been completed. Rate the {clientRole.toLowerCase()} and team members.
          </p>
        </div>

        {/* Client rating card */}
        {client && (
          <EvaluationCard
            name={
              client?.profiles?.client?.name ?? client?.displayName ?? clientRole
            }
            role={clientRole}
            avatarUrl={clientAvatarUrl}
            rating={clientRating}
            onRatingChange={setClientRating}
          />
        )}

        {/* Team member rating cards */}
        {members.map((member) =>
          member.userId ? (
            <EvaluationCard
              key={member.userId}
              name={member.name}
              role={member.role ?? workerRole}
              avatarUrl={
                member.imageUrl !== '/images/none.png'
                  ? member.imageUrl
                  : undefined
              }
              rating={teamRatings[member.userId] ?? 0}
              onRatingChange={(score) =>
                handleTeamRating(member.userId!, score)
              }
            />
          ) : null
        )}

        {/* Submit */}
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={submitRatings.isPending}
          disabled={submitRatings.isPending || !allRated}
          className="mt-6 w-full"
        >
          Submit Ratings
        </Button>

        {!allRated && (
          <p className="mt-3 text-center text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            Please rate all participants before submitting.
          </p>
        )}

        {submitRatings.error && (
          <p className="mt-3 text-center text-sm text-red-400">
            {submitRatings.error.message}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evaluation Card
// ---------------------------------------------------------------------------

interface EvaluationCardProps {
  name: string;
  role: string;
  avatarUrl?: string;
  rating: number;
  onRatingChange: (value: number) => void;
}

function EvaluationCard({
  name,
  role,
  avatarUrl,
  rating,
  onRatingChange,
}: EvaluationCardProps): JSX.Element {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="mb-4 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
      <div className="mb-4 flex items-center gap-4">
        {/* 56px avatar */}
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border-2 border-[var(--base-border,#3d3d3d)] bg-[var(--base-fill-2,#3d3d3d)]">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/images/none.png';
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <i className="ri-user-line text-2xl text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]" />
            </div>
          )}
        </div>
        <div>
          <p className="text-base font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
            {name}
          </p>
          <p className="text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            {role}
          </p>
        </div>
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => {
          const starValue = i + 1;
          const filled = starValue <= (hovered || rating);
          return (
            <button
              key={i}
              type="button"
              aria-label={`${starValue} stars for ${name}`}
              aria-pressed={rating === starValue}
              onClick={() => onRatingChange(starValue)}
              onMouseEnter={() => setHovered(starValue)}
              onMouseLeave={() => setHovered(0)}
              className="p-1 transition-transform hover:scale-110 focus:outline-none"
            >
              <svg
                className={`h-8 w-8 transition-colors ${
                  filled
                    ? 'text-[var(--state-brand-active,#36d399)]'
                    : 'text-[rgba(255,255,255,0.15)]'
                }`}
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M8 1.333l1.885 4.347 4.782.427-3.614 3.08 1.117 4.646L8 11.347l-4.17 2.486 1.117-4.646-3.614-3.08 4.782-.427L8 1.333z" />
              </svg>
            </button>
          );
        })}
        <span className="ml-3 text-sm font-medium text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
          {rating}/5
        </span>
      </div>
    </div>
  );
}
